import { initializeApp } from "firebase/app"
import {
  browserSessionPersistence,
  getAuth,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  initializeFirestore,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { ThoughtInput, ThoughtPost } from "../thoughts/props";

const FIREBASE_API_KEY = "AIzaSyCLQGqFHy1fS_wlsCjskKc_aGXgbQ0LqMI"
const FIREBASE_AUTH_DOMAIN = "personal-website-303e5.firebaseapp.com"
const FIREBASE_PROJECT_ID = "personal-website-303e5"
const FIREBASE_STORAGE_BUCKET = "personal-website-303e5.appspot.com"

const PROTECTED_ROUTE_COLLECTION = "protectedRoute"
const THOUGHT_COLLECTION = "thoughts"

const firebaseConfig = {
  apiKey: FIREBASE_API_KEY,
  authDomain: FIREBASE_AUTH_DOMAIN,
  projectId: FIREBASE_PROJECT_ID,
  storageBucket: FIREBASE_STORAGE_BUCKET,
};

const firebaseApp = initializeApp(firebaseConfig);

const auth = getAuth(firebaseApp);
const firestore = initializeFirestore(firebaseApp, {
  experimentalForceLongPolling: true,
});
const storage = getStorage(firebaseApp);

let hasFetchProtectedRoutes = false;
const protectedRoutes = [] as String[];

export const observeAuthentication = (
  onChange: (isLoggedIn: boolean) => void
) => onAuthStateChanged(auth, (user) => onChange(Boolean(user)));

export const loginWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  await signInWithEmailAndPassword(auth, email.trim(), password);
};

export const loginWithPortfolioPassword = async (password: string) => {
  const portfolioReaderEmail =
    process.env.REACT_APP_PORTFOLIO_READER_EMAIL ||
    "freddy11sanjaya@gmail.com";

  await setPersistence(auth, browserSessionPersistence);
  await signInWithEmailAndPassword(
    auth,
    portfolioReaderEmail.trim(),
    password
  );
};

export const getProtectedRoutes = async () => {
  if (hasFetchProtectedRoutes) {
    return protectedRoutes;
  }

  try {
    const querySnapshot = await getDocs(collection(firestore, PROTECTED_ROUTE_COLLECTION));
    const routes = querySnapshot.docs.map(doc => doc.data());
    const protectedUrls = routes.map(route => route.url || "") as String[];

    protectedRoutes.push(...protectedUrls);
    hasFetchProtectedRoutes = true;
  } catch (error) {
    hasFetchProtectedRoutes = false;
  } 

  return protectedRoutes;
}

const createSlug = (title: string) => {
  const slug = title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return slug || `thought-${Date.now()}`;
};

const normalizeThought = (slug: string, data: any): ThoughtPost => ({
  slug,
  title: data.title || "Untitled",
  description: data.description || "",
  body: data.body || "",
  imageUrl: data.imageUrl || "",
  published: Boolean(data.published),
  publishedAt: data.publishedAt || data.updatedAt || "",
  updatedAt: data.updatedAt || "",
});

const getPublishedThoughtsQuery = () =>
  query(
    collection(firestore, THOUGHT_COLLECTION),
    where("published", "==", true)
  );

const normalizePublishedThoughts = (documents: any[]): ThoughtPost[] =>
  documents
    .map((thought) => normalizeThought(thought.id, thought.data()))
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));

export const getPublishedThoughts = async (): Promise<ThoughtPost[]> => {
  const snapshot = await getDocs(getPublishedThoughtsQuery());
  return normalizePublishedThoughts(snapshot.docs);
};

export const subscribeToPublishedThoughts = (
  onChange: (thoughts: ThoughtPost[]) => void,
  onError: () => void
) => {
  return onSnapshot(
    getPublishedThoughtsQuery(),
    (snapshot) => {
      onChange(normalizePublishedThoughts(snapshot.docs));
    },
    onError
  );
};

const normalizeAdminThoughts = (documents: any[]): ThoughtPost[] =>
  documents
    .map((thought) => normalizeThought(thought.id, thought.data()))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

export const subscribeToAdminThoughts = (
  onChange: (thoughts: ThoughtPost[]) => void,
  onError: () => void
) =>
  onSnapshot(
    collection(firestore, THOUGHT_COLLECTION),
    (snapshot) => onChange(normalizeAdminThoughts(snapshot.docs)),
    onError
  );

export const getThoughtBySlug = async (
  slug: string
): Promise<ThoughtPost | null> => {
  try {
    const snapshot = await getDoc(doc(firestore, THOUGHT_COLLECTION, slug));
    if (!snapshot.exists()) {
      return null;
    }
    return normalizeThought(snapshot.id, snapshot.data());
  } catch (error) {
    return null;
  }
};

export const saveThought = async (
  input: ThoughtInput,
  existingThought?: Pick<ThoughtPost, "slug" | "publishedAt">
): Promise<ThoughtPost> => {
  const slug = existingThought?.slug || createSlug(input.title);
  const now = new Date().toISOString();
  const thought: ThoughtPost = {
    ...input,
    slug,
    imageUrl: input.imageUrl || "",
    publishedAt: input.published
      ? existingThought?.publishedAt || now
      : "",
    updatedAt: now,
  };

  await setDoc(doc(firestore, THOUGHT_COLLECTION, slug), thought, {
    merge: true,
  });
  return thought;
};

export const deleteThought = (slug: string) =>
  deleteDoc(doc(firestore, THOUGHT_COLLECTION, slug));

export const uploadThoughtImage = async (file: File): Promise<string> => {
  const safeName = file.name
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, "-")
    .replace(/(^-|-$)/g, "");
  const imageReference = ref(
    storage,
    `thoughts/${Date.now()}-${safeName || "image"}`
  );
  await uploadBytes(imageReference, file, { contentType: file.type });
  return getDownloadURL(imageReference);
};
